/**
 * useSwap — full swap lifecycle hook.
 *
 * Orchestrates: validate → estimate gas → submit tx → wait for receipt → track analytics.
 * Uses buildSwapArgs for V4Planner → RoutePlanner → Universal Router encoding.
 *
 * ARCH-25: Transaction lifecycle state machine
 * ARCH-27: Contract interaction hook pattern
 * Story 5.3 Task 2
 */

import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { getPublicClient, getWalletClient } from "wagmi/actions";

import { universalRouterAbi } from "../../lib/abis/universal-router";
import { trackClientEvent } from "../../lib/analytics";
import { getChainAddresses } from "../../lib/chain-addresses";
import { isUserRejection, mapChainError } from "../../lib/chain-errors";
import { isHexAddress } from "../../lib/hex-utils";
import { type SwapParams, buildSwapArgs } from "../../lib/swap-execution";
import { getActiveChain, wagmiConfig } from "../../lib/wagmi-config";
import { tokenKeys } from "../api/query-keys";
import { useWallet } from "../use-wallet";
import type { TransactionState } from "./use-transaction-state";
import { useTransactionState } from "./use-transaction-state";

/** Extract the RPC URL from a viem transport object without type assertions */
function extractTransportUrl(transport: unknown): string | undefined {
	if (typeof transport !== "object" || transport === null || !("url" in transport)) {
		return undefined;
	}
	const obj: { url: unknown } = transport;
	return typeof obj.url === "string" ? obj.url : undefined;
}

/** Validate wallet + chain prerequisites; returns error string or valid context. */
function validateSwapPrereqs(address: string | undefined):
	| { ok: false; error: string }
	| {
			ok: true;
			activeChain: ReturnType<typeof getActiveChain>;
			addresses: NonNullable<ReturnType<typeof getChainAddresses>>;
			publicClient: NonNullable<ReturnType<typeof getPublicClient>>;
			validAddress: `0x${string}`;
	  } {
	if (!address || !isHexAddress(address)) {
		return { ok: false, error: "Wallet not connected. Please connect your wallet." };
	}

	const activeChain = getActiveChain();
	const addresses = getChainAddresses(activeChain.id);
	if (!addresses) {
		return { ok: false, error: `Unsupported chain: ${activeChain.id}` };
	}

	const publicClient = getPublicClient(wagmiConfig, { chainId: activeChain.id });
	if (!publicClient) {
		return { ok: false, error: "Network connection unavailable." };
	}

	return { ok: true, activeChain, addresses, publicClient, validAddress: address };
}

/** Estimate gas for the swap; returns an error message on failure, undefined on success. */
async function estimateSwapGas(estimateFn: () => Promise<unknown>): Promise<string | undefined> {
	try {
		await estimateFn();
		return undefined;
	} catch (gasError: unknown) {
		// Log the full error for debugging — mapChainError only keeps the user-friendly message
		console.warn("[swap] Gas estimation failed — raw error:", gasError);
		return mapChainError(gasError);
	}
}

/** Analytics context for failed/rejected swaps. */
type SwapErrorContext = {
	tokenId: string;
	direction: "buy" | "sell";
	amountIn: string;
};

/** Classify and handle a swap error — user rejection vs chain failure. */
function handleSwapError(
	error: unknown,
	setFailed: (msg: string) => void,
	setRejected: () => void,
	context: SwapErrorContext
): void {
	if (isUserRejection(error)) {
		setRejected();
		trackClientEvent("swap_rejected", context);
	} else {
		const errorMessage = mapChainError(error);
		setFailed(errorMessage);
		trackClientEvent("swap_failed", { ...context, errorMessage });
	}
}

export type SwapExecuteParams = SwapParams & {
	/** Token ID for analytics and cache invalidation */
	tokenId: string;
	/** Buy or sell direction for analytics */
	direction: "buy" | "sell";
};

type UseSwapReturn = {
	execute: (params: SwapExecuteParams) => Promise<void>;
	txState: TransactionState;
	reset: () => void;
};

/**
 * Hook for executing token swaps via the Universal Router.
 *
 * Full lifecycle: preparing → gas estimate → awaiting_signature → pending → confirmed/failed/rejected
 */
export function useSwap(): UseSwapReturn {
	const {
		state: txState,
		setPreparing,
		setAwaitingSignature,
		setPending,
		setConfirmed,
		setFailed,
		setRejected,
		reset,
	} = useTransactionState();

	const queryClient = useQueryClient();
	const { address } = useWallet();

	const execute = useCallback(
		async (params: SwapExecuteParams) => {
			try {
				setPreparing();

				const prereqs = validateSwapPrereqs(address);
				if (!prereqs.ok) {
					setFailed(prereqs.error);
					return;
				}
				const { activeChain, addresses, publicClient, validAddress } = prereqs;

				// Auto-detect native ETH input when tokenIn matches WETH
				const isNativeEth =
					params.isNativeEthInput ?? params.tokenIn.toLowerCase() === addresses.weth.toLowerCase();

				// Build structured swap args for Universal Router execute
				const { commands, inputs, deadline, value } = buildSwapArgs({
					...params,
					isNativeEthInput: isNativeEth,
				});

				// Gas estimation — prevents submitting a tx that would revert
				const gasError = await estimateSwapGas(() =>
					publicClient.estimateContractGas({
						address: addresses.universalRouter,
						abi: universalRouterAbi,
						functionName: "execute",
						args: [commands, inputs, deadline],
						value,
						account: validAddress,
					})
				);
				if (gasError) {
					setFailed(gasError);
					return;
				}

				setAwaitingSignature();

				// Detect Anvil fork: check if RPC URL is localhost
				const rpcUrl = extractTransportUrl(publicClient.transport);
				const isAnvilFork =
					!!rpcUrl && (rpcUrl.includes("localhost") || rpcUrl.includes("127.0.0.1"));

				let txHash: `0x${string}`;

				if (isAnvilFork) {
					// On Anvil fork, use impersonation + unsigned tx.
					// MetaMask's signed eth_sendRawTransaction reverts on Anvil while
					// unsigned eth_sendTransaction (impersonated) succeeds — Anvil fork quirk.
					await fetch(rpcUrl, {
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({
							jsonrpc: "2.0",
							method: "anvil_impersonateAccount",
							params: [validAddress],
							id: 1,
						}),
					});

					const { createWalletClient: viemCreateWalletClient, http: viemHttp } = await import(
						"viem"
					);
					const impersonatedWallet = viemCreateWalletClient({
						account: validAddress,
						chain: publicClient.chain,
						transport: viemHttp(rpcUrl),
					});

					txHash = await impersonatedWallet.writeContract({
						chain: activeChain,
						address: addresses.universalRouter,
						abi: universalRouterAbi,
						functionName: "execute",
						args: [commands, inputs, deadline],
						value,
					});

					await fetch(rpcUrl, {
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({
							jsonrpc: "2.0",
							method: "anvil_stopImpersonatingAccount",
							params: [validAddress],
							id: 1,
						}),
					});
				} else {
					// Production path: use the normal wallet client from wagmi
					const walletClient = await getWalletClient(wagmiConfig, {
						chainId: activeChain.id,
					});

					txHash = await walletClient.writeContract({
						address: addresses.universalRouter,
						abi: universalRouterAbi,
						functionName: "execute",
						args: [commands, inputs, deadline],
						value,
					});
				}

				setPending(txHash);

				// Wait for on-chain confirmation
				const receipt = await publicClient.waitForTransactionReceipt({
					hash: txHash,
				});

				if (receipt.status === "reverted") {
					console.warn("[swap] Transaction reverted on-chain:", {
						txHash,
						blockNumber: String(receipt.blockNumber ?? ""),
						gasUsed: String(receipt.gasUsed ?? ""),
					});
					setFailed("Transaction failed on-chain. The swap was reverted.");
					return;
				}

				setConfirmed();

				// Track analytics
				trackClientEvent("swap_executed", {
					tokenId: params.tokenId,
					direction: params.direction,
					amountIn: params.amountIn.toString(),
					amountOutMin: params.amountOutMinimum.toString(),
					txHash,
				});

				// Invalidate TanStack Query caches
				await Promise.all([
					queryClient.invalidateQueries({
						queryKey: tokenKeys.detail(params.tokenId),
					}),
					queryClient.invalidateQueries({
						queryKey: tokenKeys.trades(params.tokenId),
					}),
					queryClient.invalidateQueries({ queryKey: ["token-balance"] }),
				]);
			} catch (error: unknown) {
				console.warn("[swap] Swap execution error:", error);
				handleSwapError(error, setFailed, setRejected, {
					tokenId: params.tokenId,
					direction: params.direction,
					amountIn: params.amountIn.toString(),
				});
			}
		},
		[
			setPreparing,
			setAwaitingSignature,
			setPending,
			setConfirmed,
			setFailed,
			setRejected,
			queryClient,
			address,
		]
	);

	return useMemo(() => ({ execute, txState, reset }), [execute, txState, reset]);
}
